/**
 * Seat-aware "Invite User" modal (AC-USER-007).
 *
 * Two paths, one form:
 *   Send invite (default) — the account is created without a password and the
 *     invitee sets one from the emailed link. The link is also shown here so an
 *     admin can hand it over directly when mail is slow.
 *   Create directly     — the admin sets a password, or generates one; the
 *     temporary password is displayed EXACTLY ONCE with a copy button.
 *
 * The seat meter comes from `GET /users/v1`'s `seats` block. When the plan is
 * full the submit button is disabled with an explanation rather than letting the
 * admin discover the 409 after typing everything out. The button is likewise
 * disabled while any validation error (client-side or a server 409 such as an
 * email already registered on another team) is showing; editing the flagged
 * field clears it and re-enables the button.
 */
import { useMemo, useState, type FormEvent } from 'react';

import { OverlayDialog } from '../../components/overlay-dialog';
import { TextField } from '../../components/text-field';
import { ApiError } from '../../lib/api-error';
import type { CreatedTeamUser, CreateTeamUserInput, SeatUsage } from '../../lib/api-types';
import { EMAIL_PATTERN, OWNER_PASSWORD_MIN_LENGTH } from '../../lib/constants';
import { PERSONA_LABELS, type Persona } from '../../lib/personas';

/** Personas a Team Admin may grant — `platform_admin` is provisioned out-of-band. */
export const INVITABLE_PERSONAS: readonly Persona[] = [
  'team_admin',
  'offensive_coordinator',
  'defensive_coordinator',
  'coach',
  'primary_stat_operator',
  'general_stat_operator',
  'fanatic',
];

const PASSWORD_ALPHABET = 'ABCDEFGHJKMNPQRSTUVWXYZabcdefghijkmnpqrstuvwxyz23456789!@#$%^&*-_=+?';

/** CSPRNG password suggestion; the server re-validates against its own policy. */
function suggestPassword(length = 20): string {
  const bytes = new Uint32Array(length);
  globalThis.crypto.getRandomValues(bytes);
  const body = Array.from(bytes, (value) => PASSWORD_ALPHABET[value % PASSWORD_ALPHABET.length]);
  // Guarantee the four character classes the server requires.
  body[0] = 'A';
  body[1] = 'z';
  body[2] = '7';
  body[3] = '!';
  return body.join('');
}

export interface InviteUserDialogProps {
  seats: SeatUsage | null;
  onCancel: () => void;
  onCreate: (input: CreateTeamUserInput) => Promise<CreatedTeamUser>;
  /** Called after the admin dismisses the success panel, to refresh the list. */
  onDone: (created: CreatedTeamUser) => void;
}

export function InviteUserDialog({
  seats,
  onCancel,
  onCreate,
  onDone,
}: InviteUserDialogProps): JSX.Element {
  const [email, setEmail] = useState('');
  const [persona, setPersona] = useState<Persona>('coach');
  const [displayName, setDisplayName] = useState('');
  const [isInvite, setIsInvite] = useState(true);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [created, setCreated] = useState<CreatedTeamUser | null>(null);
  const [isCopied, setIsCopied] = useState(false);

  // Fanatics are unlimited on every plan, so the cap only blocks staff personas.
  const isSeatBlocked = useMemo(() => {
    if (seats === null || seats.seats_limit === null || persona === 'fanatic') return false;
    return seats.seats_used >= seats.seats_limit;
  }, [persona, seats]);

  function validate(
    values: { email: string; password: string; confirmPassword: string; isInvite: boolean } = {
      email,
      password,
      confirmPassword,
      isInvite,
    }
  ): Record<string, string> {
    const errors: Record<string, string> = {};
    const trimmed = values.email.trim();
    if (trimmed === '') errors.email = 'Enter an email address.';
    else if (!EMAIL_PATTERN.test(trimmed)) errors.email = 'Enter a valid email address.';
    if (!values.isInvite) {
      if (values.password.length < OWNER_PASSWORD_MIN_LENGTH) {
        errors.password = `Use at least ${OWNER_PASSWORD_MIN_LENGTH} characters.`;
      } else if (values.password !== values.confirmPassword) {
        errors.confirmPassword = 'Passwords do not match.';
      }
    }
    return errors;
  }

  /**
   * Any edit clears the submission-level error (it described the values that
   * were just sent) and re-checks only the fields that were already flagged:
   * a server error (e.g. 409 "email already has an account") clears once the
   * field changes, a client error clears once the value is actually valid.
   * While any error stands the submit button stays disabled.
   */
  function revalidateAfterEdit(
    next: Partial<{ email: string; password: string; confirmPassword: string; isInvite: boolean }>
  ): void {
    setFormError(null);
    setFieldErrors((current) => {
      const flagged = Object.keys(current);
      if (flagged.length === 0) return current;
      const fresh = validate({ email, password, confirmPassword, isInvite, ...next });
      const kept: Record<string, string> = {};
      for (const key of flagged) {
        const changed =
          (key === 'email' && next.email !== undefined) ||
          ((key === 'password' || key === 'confirmPassword') &&
            (next.password !== undefined ||
              next.confirmPassword !== undefined ||
              next.isInvite !== undefined));
        if (!changed) {
          kept[key] = current[key] as string;
        } else if (fresh[key] !== undefined) {
          kept[key] = fresh[key] as string;
        }
      }
      return kept;
    });
  }

  const hasErrors = Object.keys(fieldErrors).length > 0 || formError !== null;

  async function handleSubmit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    const errors = validate();
    setFieldErrors(errors);
    setFormError(null);
    if (Object.keys(errors).length > 0) return;

    setIsSubmitting(true);
    try {
      const payload: CreateTeamUserInput = {
        email: email.trim().toLowerCase(),
        persona,
        send_invite: isInvite,
      };
      if (displayName.trim() !== '') payload.display_name = displayName.trim();
      if (!isInvite) payload.password = password;
      setCreated(await onCreate(payload));
    } catch (error) {
      if (ApiError.isApiError(error)) {
        setFormError(error.message);
        if (error.field !== null) setFieldErrors({ [error.field]: error.message });
      } else {
        setFormError('Something went wrong creating the account. Please try again.');
      }
    } finally {
      setIsSubmitting(false);
    }
  }

  async function copy(value: string): Promise<void> {
    try {
      await globalThis.navigator?.clipboard?.writeText(value);
      setIsCopied(true);
    } catch {
      setIsCopied(false);
    }
  }

  if (created !== null) {
    const secret = created.temporary_password ?? created.invite_url ?? '';
    return (
      <OverlayDialog title="User created" onClose={() => onDone(created)}>
        <div className="stack invite-result">
          <p>
            <strong>{created.email}</strong> was added as{' '}
            {PERSONA_LABELS[created.persona ?? 'coach']}.
          </p>
          {created.temporary_password !== null ? (
            <p className="muted">
              This temporary password is shown once. Share it over a secure channel and ask them to
              change it at first sign-in.
            </p>
          ) : (
            <p className="muted">
              An invite email is on its way. The link below works for{' '}
              {created.invite_expires_in_days ?? 14} days if you would rather send it yourself.
            </p>
          )}
          <code className="invite-result__secret mono">{secret}</code>
          <div className="btn-row">
            <button type="button" className="btn btn--secondary" onClick={() => void copy(secret)}>
              {isCopied ? 'Copied' : 'Copy to clipboard'}
            </button>
            <button type="button" className="btn btn--primary" onClick={() => onDone(created)}>
              Done
            </button>
          </div>
        </div>
      </OverlayDialog>
    );
  }

  return (
    <OverlayDialog title="Invite User" onClose={onCancel}>
      <form className="stack" onSubmit={(event) => void handleSubmit(event)} noValidate>
        {seats !== null && seats.seats_limit !== null ? (
          <p className="muted" data-testid="seat-meter">
            {seats.seats_used} of {seats.seats_limit} staff seats used
            {seats.plan ? ` on the ${seats.plan} plan` : ''}. Fanatic accounts are unlimited.
          </p>
        ) : null}

        <TextField
          label="Email"
          type="email"
          name="email"
          autoComplete="off"
          required
          value={email}
          onChange={(event) => {
            setEmail(event.target.value);
            revalidateAfterEdit({ email: event.target.value });
          }}
          error={fieldErrors.email ?? null}
        />

        <TextField
          label="Name (optional)"
          name="display_name"
          value={displayName}
          onChange={(event) => {
            setDisplayName(event.target.value);
            revalidateAfterEdit({});
          }}
        />

        <div className="field">
          <label className="field__label" htmlFor="invite-persona">
            Persona
          </label>
          <select
            id="invite-persona"
            className="field__control"
            value={persona}
            onChange={(event) => {
              setPersona(event.target.value as Persona);
              revalidateAfterEdit({});
            }}
          >
            {INVITABLE_PERSONAS.map((option) => (
              <option key={option} value={option}>
                {PERSONA_LABELS[option]}
              </option>
            ))}
          </select>
        </div>

        <label className="checkbox">
          <input
            type="checkbox"
            checked={isInvite}
            onChange={(event) => {
              setIsInvite(event.target.checked);
              revalidateAfterEdit({ isInvite: event.target.checked });
            }}
          />
          <span className="checkbox__text">
            Send an invite email — they choose their own password
          </span>
        </label>

        {!isInvite ? (
          <>
            <TextField
              label="Temporary password"
              type="text"
              name="password"
              autoComplete="off"
              value={password}
              onChange={(event) => {
                setPassword(event.target.value);
                revalidateAfterEdit({ password: event.target.value });
              }}
              error={fieldErrors.password ?? null}
              hint={`At least ${OWNER_PASSWORD_MIN_LENGTH} characters with an uppercase letter, a digit and a symbol.`}
            />
            <TextField
              label="Confirm password"
              type="text"
              name="confirm_password"
              autoComplete="off"
              value={confirmPassword}
              onChange={(event) => {
                setConfirmPassword(event.target.value);
                revalidateAfterEdit({ confirmPassword: event.target.value });
              }}
              error={fieldErrors.confirmPassword ?? null}
            />
            <button
              type="button"
              className="btn btn--ghost btn--sm"
              onClick={() => {
                const generated = suggestPassword();
                setPassword(generated);
                setConfirmPassword(generated);
                revalidateAfterEdit({ password: generated, confirmPassword: generated });
              }}
            >
              Generate a password
            </button>
          </>
        ) : null}

        {isSeatBlocked ? (
          <div className="banner banner--warning" role="alert">
            <p>
              All {seats?.seats_limit} staff seats are in use. Remove a member or upgrade the plan
              to add another {PERSONA_LABELS[persona]}.
            </p>
          </div>
        ) : null}

        {formError !== null ? (
          <div className="banner banner--error" role="alert">
            <p>{formError}</p>
          </div>
        ) : null}

        <div className="btn-row dialog-actions">
          <button type="button" className="btn btn--secondary" onClick={onCancel}>
            Cancel
          </button>
          <button
            type="submit"
            className="btn btn--primary"
            disabled={isSubmitting || isSeatBlocked || hasErrors}
          >
            {isSubmitting ? 'Working…' : isInvite ? 'Send Invite' : 'Create User'}
          </button>
        </div>
      </form>
    </OverlayDialog>
  );
}
